'use client';

import { FormEvent, useEffect, useState } from 'react';

type SearchResultItem = {
  id: string;
  storageKey?: string;
  title: string;
  productId?: string;
  condition?: string;
  itemWebUrl?: string;
  imageUrl?: string;
  importedFrom?: string;
  price?: {
    value: string | number;
    currency?: string;
  };
  tradeInCash?: {
    value: string | number;
    currency?: string;
  };
  tradeInVoucher?: {
    value: string | number;
    currency?: string;
  };
};

type SourceResult =
  | { ok: true; items: SearchResultItem[] }
  | { ok: false; error: string };

type SearchResponse = {
  query: string;
  ebay: SourceResult;
  bestbuy?: SourceResult;
};

type ImportedPayload = {
  source?: string;
  pageUrl?: string;
  exportedAt?: string;
  itemCount?: number;
  items?: unknown;
};

const CEX_SNIPPET_PATH = '/cex-webuy-console-snippet.js';
const CEX_USERSCRIPT_PATH = '/cex-webuy-send-to-flipping.user.js';

export default function HomePage() {
  const [query, setQuery] = useState('iphone 15');
  const [data, setData] = useState<SearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [cexJson, setCexJson] = useState('');
  const [cexImported, setCexImported] = useState<SearchResultItem[]>([]);
  const [cexAutoImported, setCexAutoImported] = useState<SearchResultItem[]>([]);
  const [cexImportMeta, setCexImportMeta] = useState<{ pageUrl?: string; exportedAt?: string } | null>(null);
  const [cexImportError, setCexImportError] = useState<string | null>(null);
  const [cexAutoStatus, setCexAutoStatus] = useState<string | null>(null);
  const [deletingCexKey, setDeletingCexKey] = useState<string | null>(null);
  const [bookmarkletMessage, setBookmarkletMessage] = useState<string | null>(null);
  const [snippetMessage, setSnippetMessage] = useState<string | null>(null);

  const bookmarkletHref = buildCexBookmarkletHref();
  const displayedCexItems = mergeImportedLists(cexImported, cexAutoImported);

  useEffect(() => {
    let cancelled = false;

    async function pull() {
      try {
        const response = await fetch('/api/cex/import', { cache: 'no-store' });
        if (!response.ok) return;
        const payload = (await response.json()) as { count?: number; items?: unknown[]; updatedAt?: string };
        const items = normalizeServerImportedItems(payload.items);
        if (cancelled) return;
        setCexAutoImported(items);
        if (payload.updatedAt || items.length > 0) {
          setCexAutoStatus(
            `Auto import: ${items.length} pozycji${payload.updatedAt ? ` | aktualizacja ${new Date(payload.updatedAt).toLocaleTimeString()}` : ''}`,
          );
        }
      } catch {
        // Silent: polling should not spam the UI.
      }
    }

    void pull();
    const timer = window.setInterval(pull, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmed = query.trim();
    if (!trimmed) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/search?q=${encodeURIComponent(trimmed)}&limit=5`, {
        method: 'GET',
      });

      const payload = (await response.json()) as SearchResponse | { error?: string };

      if (!response.ok) {
        throw new Error(
          typeof payload === 'object' && payload && 'error' in payload && payload.error
            ? payload.error
            : 'Request failed',
        );
      }

      setData(payload as SearchResponse);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      setData(null);
    } finally {
      setLoading(false);
    }
  }

  function onImportCexJson() {
    setCexImportError(null);
    try {
      const parsed = JSON.parse(cexJson) as ImportedPayload | unknown[];
      const { items, meta } = normalizeImportedCex(parsed);
      setCexImported(items);
      setCexImportMeta(meta);
    } catch (err) {
      setCexImported([]);
      setCexImportMeta(null);
      setCexImportError(err instanceof Error ? err.message : 'Invalid JSON');
    }
  }

  function onClearCexImport() {
    setCexJson('');
    setCexImported([]);
    setCexImportMeta(null);
    setCexImportError(null);
  }

  async function onRefreshCexAutoImport() {
    try {
      const response = await fetch('/api/cex/import', { cache: 'no-store' });
      const payload = (await response.json()) as { items?: unknown[]; updatedAt?: string };
      const items = normalizeServerImportedItems(payload.items);
      setCexAutoImported(items);
      setCexAutoStatus(
        `Auto import: ${items.length} pozycji${payload.updatedAt ? ` | aktualizacja ${new Date(payload.updatedAt).toLocaleTimeString()}` : ''}`,
      );
    } catch (err) {
      setCexAutoStatus(`Blad odswiezania auto-importu${err instanceof Error ? `: ${err.message}` : ''}`);
    }
  }

  async function onClearCexAutoImport() {
    try {
      const response = await fetch('/api/cex/import', { method: 'DELETE' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setCexAutoImported([]);
      setCexAutoStatus('Auto import wyczyszczony.');
    } catch (err) {
      setCexAutoStatus(`Blad czyszczenia auto-importu${err instanceof Error ? `: ${err.message}` : ''}`);
    }
  }

  async function onDeleteCexItem(item: SearchResultItem) {
    const localKey = importKey(item);
    setDeletingCexKey(localKey);
    try {
      if (item.storageKey || item.productId) {
        const query = new URLSearchParams();
        if (item.storageKey) query.set('storageKey', item.storageKey);
        else if (item.productId) query.set('productId', item.productId);

        const response = await fetch(`/api/cex/import?${query.toString()}`, { method: 'DELETE' });
        if (!response.ok) {
          const payload = (await response.json().catch(() => ({}))) as { error?: string };
          throw new Error(payload.error ?? `HTTP ${response.status}`);
        }

        setCexAutoImported((prev) => prev.filter((candidate) => importKey(candidate) !== localKey));
        setCexAutoStatus('Item usuniety z auto-importu (SQLite).');
      } else {
        setCexImported((prev) => prev.filter((candidate) => importKey(candidate) !== localKey));
        setCexAutoStatus('Item usuniety z manualnego importu.');
      }
    } catch (err) {
      setCexAutoStatus(`Blad usuwania itemu${err instanceof Error ? `: ${err.message}` : ''}`);
    } finally {
      setDeletingCexKey(null);
    }
  }

  async function onCopyBookmarklet() {
    setBookmarkletMessage(null);
    try {
      await navigator.clipboard.writeText(bookmarkletHref);
      setBookmarkletMessage('Bookmarklet skopiowany. Wklej go jako URL nowej zakladki.');
    } catch {
      setBookmarkletMessage('Nie udalo sie skopiowac automatycznie. Skopiuj link recznie z pola ponizej.');
    }
  }

  async function onCopySnippet() {
    setSnippetMessage(null);
    try {
      const response = await fetch(CEX_SNIPPET_PATH, { cache: 'no-store' });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const snippet = await response.text();
      await navigator.clipboard.writeText(snippet);
      setSnippetMessage('Snippet skopiowany. Wklej go w DevTools -> Console na stronie CeX.');
    } catch (error) {
      setSnippetMessage(
        `Nie udalo sie skopiowac snippetu automatycznie${error instanceof Error ? ` (${error.message})` : ''}. Otworz plik i skopiuj recznie.`,
      );
    }
  }

  return (
    <main className="page-shell">
      <section className="hero-card">
        <p className="eyebrow">Flipping</p>
        <h1>eBay API + CeX browser export</h1>
        <p className="subtitle">
          eBay dziala przez API po stronie serwera. CeX importujesz legalnie z przegladarki: otwierasz strone CeX, uruchamiasz snippet w
          konsoli i wklejasz JSON tutaj.
        </p>

        <form className="search-form" onSubmit={onSubmit}>
          <label className="field-label" htmlFor="query">
            Fraza wyszukiwania (eBay API)
          </label>
          <div className="field-row">
            <input
              id="query"
              className="search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="np. iphone 15, ps5, switch oled"
            />
            <button className="search-button" type="submit" disabled={loading}>
              {loading ? 'Szukam...' : 'Szukaj eBay'}
            </button>
          </div>
        </form>

        <section className="import-panel">
          <div className="results-panel-head">
            <h2>CeX Browser Export (manual import)</h2>
          </div>
          <ol className="step-list">
            <li>Otworz wynik wyszukiwania na stronie CeX w swojej przegladarce.</li>
            <li>Najmniej klikow: zainstaluj userscript i klikaj `Send to Flipping` na stronach listy oraz produktu.</li>
            <li>Najwygodniej: dodaj bookmarklet `CeX -{'>'} Flipping Export` (ponizej) i kliknij go na stronie CeX.</li>
            <li>Fallback: otworz DevTools -{'>'} Console i wklej skrypt z pliku <code>{CEX_SNIPPET_PATH}</code>.</li>
            <li>Skopiowany JSON wklej ponizej i kliknij import.</li>
          </ol>
          <div className="bookmarklet-box">
            <p className="field-label">Userscript (Tampermonkey) - auto send do Flipping</p>
            <p className="muted">
              Zainstaluj Tampermonkey, otworz plik userscriptu i kliknij install. Na CeX pojawi sie przycisk `Send to Flipping`.
            </p>
            <div className="button-row">
              <a className="ghost-button bookmarklet-link" href={CEX_USERSCRIPT_PATH} target="_blank" rel="noreferrer">
                Otworz userscript (.user.js)
              </a>
              <button type="button" className="ghost-button" onClick={onRefreshCexAutoImport}>
                Sync auto imports
              </button>
              <button type="button" className="ghost-button" onClick={onClearCexAutoImport}>
                Clear auto imports
              </button>
            </div>
            {cexAutoStatus ? <p className="status">{cexAutoStatus}</p> : null}
          </div>
          <div className="bookmarklet-box">
            <p className="field-label">Bookmarklet (CeX -{'>'} Flipping Export)</p>
            <p className="muted">
              Przeciagnij link na pasek zakladek albo skopiuj URL bookmarkletu i wklej jako adres nowej zakladki.
            </p>
            <div className="button-row">
              <a className="ghost-button bookmarklet-link" href={bookmarkletHref}>
                CeX -{'>'} Flipping Export
              </a>
              <button type="button" className="search-button" onClick={onCopyBookmarklet}>
                Copy Bookmarklet URL
              </button>
              <button type="button" className="ghost-button" onClick={onCopySnippet}>
                Copy Snippet
              </button>
            </div>
            <textarea className="json-input bookmarklet-textarea" readOnly value={bookmarkletHref} spellCheck={false} />
            {bookmarkletMessage ? <p className="status">{bookmarkletMessage}</p> : null}
            {snippetMessage ? <p className="status">{snippetMessage}</p> : null}
          </div>
          <p className="muted import-link-line">
            Snippet: <a href={CEX_SNIPPET_PATH} target="_blank" rel="noreferrer">otworz plik</a>
          </p>
          <textarea
            className="json-input"
            value={cexJson}
            onChange={(e) => setCexJson(e.target.value)}
            placeholder="Wklej JSON z console snippet (CeX browser export)..."
            spellCheck={false}
          />
          <div className="button-row">
            <button type="button" className="search-button" onClick={onImportCexJson}>
              Importuj CeX JSON
            </button>
            <button type="button" className="ghost-button" onClick={onClearCexImport}>
              Wyczyść
            </button>
          </div>
          {cexImportError ? <p className="status error">Blad importu: {cexImportError}</p> : null}
          {cexImportMeta ? (
            <p className="status">
              Zaimportowano {cexImported.length} pozycji
              {cexImportMeta.exportedAt ? ` | ${new Date(cexImportMeta.exportedAt).toLocaleString()}` : ''}
            </p>
          ) : null}
        </section>

        {error ? <p className="status error">Blad: {error}</p> : null}
        {!error && loading ? <p className="status">Pobieranie wynikow...</p> : null}

        <div className="results-grid">
          <ResultColumn title="eBay (API)" result={data?.ebay} />
          <ResultColumn
            title="CeX (Imported)"
            result={{ ok: true, items: displayedCexItems }}
            emptyMessage="Brak importu CeX (manual lub auto)."
            onDeleteItem={onDeleteCexItem}
            deletingItemKey={deletingCexKey}
          />
        </div>
      </section>
    </main>
  );
}

function ResultColumn({
  title,
  result,
  emptyMessage,
  onDeleteItem,
  deletingItemKey,
}: {
  title: string;
  result?: SourceResult;
  emptyMessage?: string;
  onDeleteItem?: (item: SearchResultItem) => void | Promise<void>;
  deletingItemKey?: string | null;
}) {
  return (
    <section className="results-panel" aria-live="polite">
      <div className="results-panel-head">
        <h2>{title}</h2>
      </div>

      {!result ? <p className="muted">{emptyMessage ?? 'Brak wynikow. Uruchom wyszukiwanie.'}</p> : null}
      {result && !result.ok ? <p className="status error">{result.error}</p> : null}
      {result && result.ok && result.items.length === 0 ? <p className="muted">{emptyMessage ?? 'Brak dopasowan.'}</p> : null}

      {result && result.ok && result.items.length > 0 ? (
        <ul className="item-list">
          {result.items.map((item, index) => (
            <li key={`${item.id}-${index}`} className="item-card">
              <div className="item-card-top">
                <p className="item-title">{item.title}</p>
                <p className="item-price">{formatPrice(item.price)}</p>
              </div>
              <div className="item-meta">
                {item.condition ? <span>{item.condition}</span> : <span>Condition: n/a</span>}
                {item.itemWebUrl ? (
                  <a href={item.itemWebUrl} target="_blank" rel="noreferrer">
                    Otworz
                  </a>
                ) : null}
                {onDeleteItem ? (
                  <button
                    type="button"
                    className="inline-delete-button"
                    onClick={() => void onDeleteItem(item)}
                    disabled={deletingItemKey === importKey(item)}
                  >
                    {deletingItemKey === importKey(item) ? 'Usuwam...' : 'Usuń'}
                  </button>
                ) : null}
              </div>
              {item.tradeInCash || item.tradeInVoucher ? (
                <div className="trade-values">
                  <p>{item.tradeInVoucher ? `Trade-in Voucher: ${formatPrice(item.tradeInVoucher)}` : 'Trade-in Voucher: n/a'}</p>
                  <p>{item.tradeInCash ? `Trade-in Cash: ${formatPrice(item.tradeInCash)}` : 'Trade-in Cash: n/a'}</p>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function formatPrice(price?: SearchResultItem['price']) {
  if (!price) return 'Brak ceny';
  return price.currency ? `${price.value} ${price.currency}` : String(price.value);
}

function normalizeImportedCex(parsed: ImportedPayload | unknown[]) {
  const payload = Array.isArray(parsed) ? { items: parsed } : parsed;
  if (!payload || typeof payload !== 'object') {
    throw new Error('JSON must be an object or array');
  }

  const rawItems = Array.isArray(payload.items) ? payload.items : [];
  if (!Array.isArray(payload.items) && !Array.isArray(parsed)) {
    throw new Error('Missing items[] in imported JSON');
  }

  const normalizedItems = rawItems
    .map((row, index) => normalizeImportedItem(row, index))
    .filter(Boolean) as SearchResultItem[];
  const items = dedupeImportedItems(normalizedItems);

  return {
    items,
    meta: {
      pageUrl: typeof payload.pageUrl === 'string' ? payload.pageUrl : undefined,
      exportedAt: typeof payload.exportedAt === 'string' ? payload.exportedAt : undefined,
    },
  };
}

function dedupeImportedItems(items: SearchResultItem[]) {
  const seen = new Set<string>();
  const deduped: SearchResultItem[] = [];

  for (const item of items) {
    const key = [
      (item.itemWebUrl ?? '').trim().toLowerCase(),
      item.title.trim().toLowerCase(),
      item.price ? `${item.price.value}|${item.price.currency ?? ''}` : '',
    ].join('||');

    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push(item);
  }

  return deduped;
}

function normalizeImportedItem(row: unknown, index: number): SearchResultItem | null {
  if (!row || typeof row !== 'object') return null;
  const obj = row as Record<string, unknown>;

  const titleCandidate = obj.title ?? obj.name ?? obj.boxName;
  const title = typeof titleCandidate === 'string' ? titleCandidate.trim() : '';
  if (!title) return null;

  const priceValue = normalizePriceValue(obj.price);
  const price = priceValue
    ? {
        value: priceValue.value,
        currency: priceValue.currency,
      }
    : undefined;

  return {
    id: String(obj.id ?? obj.itemWebUrl ?? index),
    title,
    condition: typeof obj.condition === 'string' ? obj.condition : 'Imported from browser',
    itemWebUrl: typeof obj.itemWebUrl === 'string' ? obj.itemWebUrl : typeof obj.url === 'string' ? obj.url : undefined,
    imageUrl: typeof obj.imageUrl === 'string' ? obj.imageUrl : typeof obj.image === 'string' ? obj.image : undefined,
    price,
    tradeInCash: normalizePriceValue(obj.tradeInCash) ?? undefined,
    tradeInVoucher: normalizePriceValue(obj.tradeInVoucher) ?? undefined,
  };
}

function normalizePriceValue(input: unknown): { value: string | number; currency?: string } | null {
  if (!input) return null;

  if (typeof input === 'number') {
    return { value: input, currency: 'GBP' };
  }

  if (typeof input === 'string') {
    const clean = input.replace(/[^0-9.,-]/g, '').trim();
    if (!clean) return null;
    const parsed = Number(clean.replace(/,/g, ''));
    return Number.isFinite(parsed) ? { value: parsed, currency: 'GBP' } : { value: input };
  }

  if (typeof input === 'object') {
    const price = input as { value?: unknown; currency?: unknown };
    const normalized = normalizePriceValue(price.value);
    if (!normalized) return null;
    return {
      value: normalized.value,
      currency: typeof price.currency === 'string' ? price.currency : normalized.currency,
    };
  }

  return null;
}

function normalizeServerImportedItems(input: unknown): SearchResultItem[] {
  if (!Array.isArray(input)) return [];

  return input
    .map((row, index) => {
      if (!row || typeof row !== 'object') return null;
      const obj = row as Record<string, unknown>;
      const title = typeof obj.title === 'string' ? obj.title.trim() : '';
      if (!title) return null;

      return {
        id: String(obj.id ?? index),
        storageKey: typeof obj.storageKey === 'string' ? obj.storageKey : undefined,
        productId: typeof obj.productId === 'string' ? obj.productId : undefined,
        title,
        condition: typeof obj.condition === 'string' ? obj.condition : undefined,
        itemWebUrl: typeof obj.itemWebUrl === 'string' ? obj.itemWebUrl : undefined,
        imageUrl: typeof obj.imageUrl === 'string' ? obj.imageUrl : undefined,
        importedFrom: typeof obj.importedFrom === 'string' ? obj.importedFrom : undefined,
        price: normalizePriceValue(obj.price) ?? undefined,
        tradeInCash: normalizePriceValue(obj.tradeInCash) ?? undefined,
        tradeInVoucher: normalizePriceValue(obj.tradeInVoucher) ?? undefined,
      } satisfies SearchResultItem;
    })
    .filter(Boolean) as SearchResultItem[];
}

function mergeImportedLists(manualItems: SearchResultItem[], autoItems: SearchResultItem[]) {
  if (autoItems.length === 0) return manualItems;
  if (manualItems.length === 0) return autoItems;

  const byKey = new Map<string, SearchResultItem>();

  for (const item of manualItems) {
    byKey.set(importKey(item), item);
  }

  for (const item of autoItems) {
    const key = importKey(item);
    const existing = byKey.get(key);
    byKey.set(
      key,
      existing
        ? {
            ...existing,
            ...item,
            price: item.price ?? existing.price,
            tradeInCash: item.tradeInCash ?? existing.tradeInCash,
            tradeInVoucher: item.tradeInVoucher ?? existing.tradeInVoucher,
          }
        : item,
    );
  }

  return Array.from(byKey.values());
}

function importKey(item: SearchResultItem) {
  return [
    (item.productId ?? '').toLowerCase(),
    (item.itemWebUrl ?? '').toLowerCase(),
    item.title.toLowerCase(),
  ].join('||');
}

function buildCexBookmarkletHref() {
  if (typeof window === 'undefined') return 'javascript:void(0)';

  const scriptUrl = new URL(CEX_SNIPPET_PATH, window.location.origin);
  scriptUrl.searchParams.set('v', '2');

  // Inject local snippet into the currently opened CeX page.
  const code =
    "(function(){try{var d=document;var s=d.createElement('script');s.src=" +
    JSON.stringify(scriptUrl.toString() + '&t=' + Date.now()) +
    ";s.async=true;(d.head||d.documentElement).appendChild(s);}catch(e){alert('Bookmarklet failed: '+(e&&e.message?e.message:e));}})();";

  return `javascript:${code}`;
}
